import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { IReservationScheduleItem, ITableAvailability } from "../models/IReservation";
import { getReservationSchedule, getTableAvailability } from "../services/reservationService";
import DateTimePicker from "../../components/DateTimePicker";
import TableFigure from "./TableFigure";

interface Props {
  restaurantId: string;
}

type TableState = "free" | "booked" | "closed";
type Filter = "all" | TableState;
type View = "cards" | "floor";

const VIEW_STORAGE_KEY = "occupancyView";

function readStoredView(): View {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === "floor" ? "floor" : "cards";
  } catch {
    return "cards";
  }
}

const DURATION_OPTIONS: { value: number; label: string }[] = [
  { value: 0, label: "Restoranın varsayılanı" },
  { value: 30, label: "30 dk" },
  { value: 60, label: "1 saat" },
  { value: 90, label: "1.5 saat" },
  { value: 120, label: "2 saat" },
];

const STATE_STYLE: Record<TableState, { label: string; color: string; bg: string; border: string; icon: string }> = {
  free:   { label: "Boş",    color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0", icon: "bi-check-circle-fill" },
  booked: { label: "Dolu",   color: "#b91c1c", bg: "#fef2f2", border: "#fecaca", icon: "bi-person-fill-lock" },
  closed: { label: "Kapalı", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb", icon: "bi-moon-fill" },
};

const ERROR_TEXT: Record<string, string> = {
  ReservationsNotEnabled: "Bu restoran rezervasyon almıyor. Doluluğu görmek için önce Ayarlar sekmesinden rezervasyonu açın.",
  RestourantAccessDenied: "Bu restoranın doluluğunu görme yetkiniz yok.",
};

const pad = (n: number) => String(n).padStart(2, "0");

/** Şu anı 5 dakikaya aşağı yuvarlayıp datetime-local biçiminde verir (DateTimePicker adımıyla aynı). */
function nowLocal(): string {
  const d = new Date();
  d.setSeconds(0, 0);
  d.setMinutes(d.getMinutes() - (d.getMinutes() % 5));
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
}

/**
 * Masa-uygunluk servisi kişi sayısı ister; 1 göndererek "masa bu kişiye yetmez" (TooSmall)
 * sonucunu devre dışı bırakıyoruz. Kalan TooLarge yalnızca "masa bu kadar küçük grup için büyük"
 * demek, yani masa aslında boş.
 */
function tableState(t: ITableAvailability): TableState {
  if (t.unavailableReason === "RestaurantClosed") return "closed";
  if (t.unavailableReason === "Booked") return "booked";
  return "free";
}

/** Masa planında kaç sandalyenin dolu çizileceği: çakışan rezervasyonlardaki en kalabalık grup. */
function seatedCount(t: ITableAvailability, scheduleById: Map<string, IReservationScheduleItem>): number {
  if (tableState(t) !== "booked") return 0;
  const sizes = t.bookings
    .map((b) => scheduleById.get(b.reservationId)?.partySize)
    .filter((n): n is number => typeof n === "number");
  // Misafir bilgisi eşlenemediyse masanın tamamını dolu göster
  return sizes.length ? Math.max(...sizes) : t.capacity;
}

const labelStyle: CSSProperties ={ fontSize: "0.72rem", fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 };

export default function OccupancyPanel({ restaurantId }: Props) {
  const [atLocal, setAtLocal] = useState(nowLocal());
  const [duration, setDuration] = useState(0);
  const [filter, setFilter] = useState<Filter>("all");
  const [view, setView] = useState<View>(readStoredView);
  const [detailId, setDetailId] = useState<string | null>(null);

  const changeView = (v: View) => {
    setView(v);
    try { localStorage.setItem(VIEW_STORAGE_KEY, v); } catch { /* tercih hatırlanmaz, sorun değil */ }
  };

  const [tables, setTables] = useState<ITableAvailability[] | null>(null);
  const [scheduleById, setScheduleById] = useState<Map<string, IReservationScheduleItem>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Geçmiş saatler de sorgulanabilsin (ör. öğlen ne kadar doluyduk) — bugünün başından itibaren
  const minDate = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }, []);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const requestRef = useRef(0);

  const load = () => {
    const at = new Date(atLocal);
    if (isNaN(at.getTime())) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    setError("");

    // Dolu masaların misafir bilgisini programdan eşliyoruz; uygunluk servisi sadece saatleri döner.
    const dayMs = 24 * 60 * 60 * 1000;
    const scheduleReq = getReservationSchedule(
      restaurantId,
      new Date(at.getTime() - dayMs).toISOString(),
      new Date(at.getTime() + dayMs).toISOString(),
    ).then((res) => res.data ?? []).catch(() => [] as IReservationScheduleItem[]);

    Promise.all([
      getTableAvailability(restaurantId, at.toISOString(), 1, duration > 0 ? duration : undefined),
      scheduleReq,
    ])
      .then(([availRes, schedule]) => {
        if (requestId !== requestRef.current) return;
        setTables(availRes.data ?? []);
        setScheduleById(new Map(schedule.map((s) => [s.id, s])));
      })
      .catch((err) => {
        if (requestId !== requestRef.current) return;
        const code = err?.response?.data?.[0]?.code;
        setTables(null);
        setError(ERROR_TEXT[code] ?? "Doluluk bilgisi alınamadı. Lütfen tekrar deneyin.");
      })
      .finally(() => {
        if (requestId === requestRef.current) setLoading(false);
      });
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, atLocal, duration]);

  const summary = useMemo(() => {
    const list = tables ?? [];
    const free = list.filter((t) => tableState(t) === "free");
    const booked = list.filter((t) => tableState(t) === "booked");
    const closed = list.length > 0 && list.every((t) => tableState(t) === "closed");
    const totalSeats = list.reduce((sum, t) => sum + t.capacity, 0);
    const freeSeats = free.reduce((sum, t) => sum + t.capacity, 0);
    const occupancy = list.length ? Math.round((booked.length / list.length) * 100) : 0;
    return { total: list.length, free: free.length, booked: booked.length, closed, totalSeats, freeSeats, occupancy };
  }, [tables]);

  const visibleTables = useMemo(() => {
    const list = (tables ?? []).slice().sort((a, b) => a.tableNumber - b.tableNumber);
    return filter === "all" ? list : list.filter((t) => tableState(t) === filter);
  }, [tables, filter]);

  const detailTable = detailId ? tables?.find((t) => t.id === detailId) ?? null : null;

  const atDate = new Date(atLocal);
  const atText = isNaN(atDate.getTime())
    ? ""
    : atDate.toLocaleString("tr-TR", { day: "2-digit", month: "long", weekday: "long", hour: "2-digit", minute: "2-digit" });

  const occupancyColor = summary.occupancy >= 80 ? "#dc2626" : summary.occupancy >= 50 ? "#d97706" : "#16a34a";

  return (
    <>
      {/* Sorgu */}
      <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 14 }}>
        <div style={{ flex: "1 1 240px", maxWidth: 320 }}>
          <label style={labelStyle}>Tarih & Saat</label>
          <DateTimePicker value={atLocal} onChange={setAtLocal} minDate={minDate} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" onClick={() => setAtLocal(nowLocal())} className="resv-chip" style={{ padding: "8px 14px" }}>
            <i className="bi bi-clock me-1"></i>Şimdi
          </button>
          <button type="button" onClick={load} disabled={loading} className="resv-chip" style={{ padding: "8px 14px" }}>
            <i className="bi bi-arrow-clockwise me-1"></i>Yenile
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 18 }}>
        <label style={labelStyle}>Oturma Süresi</label>
        <div className="resv-chips">
          {DURATION_OPTIONS.map((o) => (
            <button key={o.value} type="button" onClick={() => setDuration(o.value)}
              className={`resv-chip${duration === o.value ? " resv-chip--active" : ""}`}>
              {o.label}
            </button>
          ))}
        </div>
        <div style={{ fontSize: "0.72rem", color: "#9ca3af", marginTop: 6 }}>
          <i className="bi bi-info-circle me-1"></i>
          Seçilen saatten itibaren bu süre boyunca onaylı bir rezervasyonla çakışan masa "Dolu" görünür (masalar arası tampon süresi dahil).
        </div>
      </div>

      {error && (
        <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 12, padding: "10px 14px", color: "#b91c1c", fontSize: "0.84rem", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <span><i className="bi bi-exclamation-circle me-1"></i>{error}</span>
          <button onClick={load} style={{ background: "none", border: "none", color: "#b91c1c", fontWeight: 700, cursor: "pointer", fontSize: "0.8rem", flexShrink: 0 }}>Tekrar Dene</button>
        </div>
      )}

      {loading && !tables ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <div className="spinner-border" style={{ color: "#0d9488" }} role="status" />
        </div>
      ) : tables && (
        <div style={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.15s" }}>
          {/* Restoran durumu */}
          <div style={{
            borderRadius: 14, border: "1.5px solid #e5e7eb", background: "#fff",
            boxShadow: "0 2px 10px rgba(0,0,0,0.05)", padding: "16px 18px", marginBottom: 14,
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: "0.72rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>Restoran durumu</div>
                <div style={{ fontWeight: 800, fontSize: "0.98rem", color: "#111827", marginTop: 2 }}>{atText}</div>
              </div>
              <span style={{
                fontSize: "0.78rem", fontWeight: 700, padding: "5px 12px", borderRadius: 20,
                background: summary.closed ? "#f3f4f6" : "#f0fdf4",
                color: summary.closed ? "#6b7280" : "#15803d",
              }}>
                <i className={`bi ${summary.closed ? "bi-moon-fill" : "bi-shop"} me-1`}></i>
                {summary.closed ? "Restoran bu saatte kapalı" : "Restoran açık"}
              </span>
            </div>

            {!summary.closed && summary.total > 0 && (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, marginTop: 14 }}>
                  <Stat label="Boş masa" value={`${summary.free} / ${summary.total}`} color="#15803d" />
                  <Stat label="Dolu masa" value={`${summary.booked} / ${summary.total}`} color="#b91c1c" />
                  <Stat label="Boş koltuk" value={`${summary.freeSeats} / ${summary.totalSeats}`} color="#0f766e" />
                  <Stat label="Doluluk" value={`%${summary.occupancy}`} color={occupancyColor} />
                </div>
                <div style={{ height: 8, borderRadius: 8, background: "#f1f5f9", marginTop: 12, overflow: "hidden" }}>
                  <div style={{ width: `${summary.occupancy}%`, height: "100%", background: occupancyColor, transition: "width 0.3s" }} />
                </div>
              </>
            )}
          </div>

          {summary.total === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
              <i className="bi bi-grid" style={{ fontSize: "2.2rem", display: "block", marginBottom: 8, opacity: 0.4 }}></i>
              <span style={{ fontSize: "0.88rem" }}>Bu restoranda kullanımda olan masa yok.</span>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                {!summary.closed ? (
                  <div className="resv-chips">
                    {([
                      ["all", `Tümü (${summary.total})`],
                      ["free", `Boş (${summary.free})`],
                      ["booked", `Dolu (${summary.booked})`],
                    ] as [Filter, string][]).map(([val, label]) => (
                      <button key={val} type="button" onClick={() => setFilter(val)}
                        className={`resv-chip${filter === val ? " resv-chip--active" : ""}`}>
                        {label}
                      </button>
                    ))}
                  </div>
                ) : <span />}

                {/* Görünüm seçici */}
                <div style={{ display: "inline-flex", background: "#f1f5f9", borderRadius: 10, padding: 3 }}>
                  {([
                    ["cards", "Kart", "bi-grid-fill"],
                    ["floor", "Masa", "bi-diagram-3-fill"],
                  ] as [View, string, string][]).map(([val, label, icon]) => (
                    <button key={val} type="button" onClick={() => changeView(val)}
                      style={{
                        padding: "6px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontWeight: 700, fontSize: "0.78rem",
                        background: view === val ? "#fff" : "transparent",
                        color: view === val ? "#0f766e" : "#6b7280",
                        boxShadow: view === val ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                      }}>
                      <i className={`bi ${icon} me-1`}></i>{label}
                    </button>
                  ))}
                </div>
              </div>

              {visibleTables.length === 0 ? (
                <div style={{ textAlign: "center", padding: "30px 0", color: "#9ca3af", fontSize: "0.86rem" }}>
                  Filtreye uyan masa yok.
                </div>
              ) : view === "cards" ? (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 12 }}>
                  {visibleTables.map((t) => {
                    const state = tableState(t);
                    const s = STATE_STYLE[state];
                    return (
                      <div key={t.id} style={{
                        borderRadius: 14, border: `1.5px solid ${s.border}`, background: "#fff",
                        boxShadow: "0 2px 10px rgba(0,0,0,0.04)", padding: "12px 14px",
                        borderTop: `4px solid ${s.color}`,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                            <span style={{
                              width: 28, height: 28, borderRadius: 8, background: s.bg, color: s.color, flexShrink: 0,
                              display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: "0.8rem",
                            }}>
                              {t.tableNumber}
                            </span>
                            <span style={{ fontWeight: 700, fontSize: "0.86rem", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {t.label || `Masa ${t.tableNumber}`}
                            </span>
                          </div>
                          <span style={{ fontSize: "0.7rem", fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>
                            <i className={`bi ${s.icon} me-1`}></i>{s.label}
                          </span>
                        </div>
                        <div style={{ fontSize: "0.74rem", color: "#6b7280", marginTop: 6 }}>
                          <i className="bi bi-people-fill me-1"></i>{t.minCapacity}–{t.capacity} kişilik
                        </div>

                        {state === "booked" && t.bookings.length > 0 && (
                          <div style={{ marginTop: 10 }}>
                            <BookingList table={t} scheduleById={scheduleById} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{
                  borderRadius: 16, border: "1.5px solid #e5e7eb", padding: 14,
                  background: "#fafaf9", backgroundImage: "radial-gradient(#e7e5e4 1px, transparent 1px)", backgroundSize: "16px 16px",
                  display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 215px), 1fr))", gap: 12,
                }}>
                  {visibleTables.map((t) => {
                    const state = tableState(t);
                    const s = STATE_STYLE[state];
                    return (
                      <button key={t.id} type="button" onClick={() => setDetailId(t.id)} className="occ-tile"
                        title={`Masa ${t.tableNumber} — ${s.label}`}
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "space-between", gap: 10,
                          padding: "14px 10px 12px", borderRadius: 14, border: "1.5px solid #e7e5e4",
                          background: "rgba(255,255,255,0.85)", cursor: "pointer", minWidth: 0,
                        }}>
                        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <TableFigure tableNumber={t.tableNumber} capacity={t.capacity} seated={seatedCount(t, scheduleById)} state={state} />
                        </div>
                        <div style={{ textAlign: "center", minWidth: 0, maxWidth: "100%" }}>
                          <div style={{ fontWeight: 700, fontSize: "0.82rem", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {t.label || `Masa ${t.tableNumber}`}
                          </div>
                          <div style={{ fontSize: "0.72rem", color: "#6b7280", marginTop: 2 }}>
                            {t.minCapacity}–{t.capacity} kişilik ·{" "}
                            <span style={{ color: s.color, fontWeight: 700 }}>{s.label}</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {view === "floor" && (
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: "0.74rem", color: "#6b7280", marginTop: 10 }}>
                  <Legend color="#16a34a" label="Boş" />
                  <Legend color="#dc2626" label="Dolu" />
                  <Legend color="#9ca3af" label="Kapalı" />
                  <span><i className="bi bi-info-circle me-1"></i>Dolu sandalyeler rezervasyondaki kişi sayısını gösterir. Detay için masaya tıklayın.</span>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {detailTable && (() => {
        const state = tableState(detailTable);
        const s = STATE_STYLE[state];
        return (
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)", padding: 16 }}
            onClick={(e) => { if (e.target === e.currentTarget) setDetailId(null); }}
          >
            <div style={{ background: "#fff", borderRadius: 20, padding: "22px 24px", width: "min(96vw, 400px)", maxHeight: "90vh", overflowY: "auto", boxShadow: "0 8px 40px rgba(0,0,0,0.18)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 14 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: "1rem", color: "#111827" }}>
                    Masa {detailTable.tableNumber}{detailTable.label ? ` — ${detailTable.label}` : ""}
                  </div>
                  <div style={{ fontSize: "0.76rem", color: "#6b7280", marginTop: 2 }}>
                    <i className="bi bi-people-fill me-1"></i>{detailTable.minCapacity}–{detailTable.capacity} kişilik · {atText}
                  </div>
                </div>
                <button onClick={() => setDetailId(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: "1.1rem" }}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>

              <div style={{ display: "flex", justifyContent: "center", padding: "10px 0 16px" }}>
                <TableFigure tableNumber={detailTable.tableNumber} capacity={detailTable.capacity} seated={seatedCount(detailTable, scheduleById)} state={state} />
              </div>

              <div style={{ textAlign: "center", marginBottom: 12 }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 700, padding: "4px 12px", borderRadius: 20, background: s.bg, color: s.color }}>
                  <i className={`bi ${s.icon} me-1`}></i>
                  {state === "free" ? "Bu saatte boş" : state === "closed" ? "Restoran bu saatte kapalı" : "Bu saatte dolu"}
                </span>
              </div>

              {state === "booked" && detailTable.bookings.length > 0 && (
                <BookingList table={detailTable} scheduleById={scheduleById} />
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
}

function BookingList({ table, scheduleById }: { table: ITableAvailability; scheduleById: Map<string, IReservationScheduleItem> }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {table.bookings.map((b) => {
        const r = scheduleById.get(b.reservationId);
        return (
          <div key={b.reservationId} style={{ background: "#fef2f2", borderRadius: 9, padding: "6px 10px", fontSize: "0.76rem", color: "#7f1d1d" }}>
            <div style={{ fontWeight: 700 }}>
              <i className="bi bi-clock me-1"></i>{formatTime(b.startsAtUtc)} – {formatTime(b.endsAtUtc)}
            </div>
            {r && (
              <div style={{ marginTop: 2, color: "#991b1b" }}>
                {r.contactName} · {r.partySize} kişi
                {r.contactPhone && (
                  <div>
                    <a href={`tel:${r.contactPhone}`} style={{ color: "inherit", textDecoration: "none" }}>
                      <i className="bi bi-telephone-fill me-1"></i>{r.contactPhone}
                    </a>
                  </div>
                )}
                {r.note && (
                  <div style={{ marginTop: 2, fontStyle: "italic" }}>
                    <i className="bi bi-chat-square-text me-1"></i>{r.note}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
      <span style={{ width: 11, height: 11, borderRadius: 3, background: color }} />{label}
    </span>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ background: "#f9fafb", borderRadius: 10, padding: "8px 12px" }}>
      <div style={{ fontSize: "0.68rem", color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: "1.05rem", fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
